import { useDeferredValue, useMemo, useState } from 'react';
import {
  PAYMENT_SOURCES,
  TRANSACTION_TYPES,
  findDuplicates,
  formatMonthLabel,
  formatMoney,
  groupByMonth,
  merchantKey,
  type Transaction,
} from '@wis/shared';
import { useApp } from '../state/AppContext.js';
import {
  Button,
  EmptyState,
  Field,
  Notice,
  Select,
  Sheet,
  TextInput,
  sourceLabel,
  typeLabel,
} from '../components/primitives.js';
import { TransactionRow } from '../components/TransactionRow.js';
import { TransactionSheet } from './TransactionSheet.js';

interface Filters {
  query: string;
  categoryId: string;
  source: string;
  account: string;
  type: string;
  from: string;
  to: string;
}

const EMPTY_FILTERS: Filters = {
  query: '',
  categoryId: '',
  source: '',
  account: '',
  type: '',
  from: '',
  to: '',
};

export function Transactions(): JSX.Element {
  const { transactions, categories, accounts, settings } = useApp();
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selected, setSelected] = useState<Transaction | null>(null);
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false);

  const deferredQuery = useDeferredValue(filters.query);
  const now = useMemo(() => new Date(), []);

  /** Ids that look like a duplicate of something else in the list. */
  const duplicateIds = useMemo(() => {
    const ids = new Set<string>();
    for (const transaction of transactions) {
      if (ids.has(transaction.id)) continue;
      for (const match of findDuplicates(transaction, transactions)) {
        ids.add(transaction.id);
        ids.add(match.transaction.id);
      }
    }
    return ids;
  }, [transactions]);

  const filtered = useMemo(() => {
    const needle = merchantKey(deferredQuery) || deferredQuery.trim().toLowerCase();

    return transactions.filter((transaction) => {
      if (showDuplicatesOnly && !duplicateIds.has(transaction.id)) return false;
      if (filters.categoryId && transaction.categoryId !== filters.categoryId) return false;
      if (filters.source && transaction.source !== filters.source) return false;
      if (filters.account && transaction.accountLabel !== filters.account) return false;
      if (filters.type && transaction.type !== filters.type) return false;
      if (filters.from && transaction.occurredAt < `${filters.from}T00:00:00.000Z`) return false;
      if (filters.to && transaction.occurredAt > `${filters.to}T23:59:59.999Z`) return false;

      if (needle) {
        const haystack = [
          transaction.merchantNormalized,
          transaction.merchantOriginal,
          transaction.note ?? '',
          transaction.accountLabel,
        ]
          .join(' ')
          .toLowerCase();
        const amountText = (transaction.amountSen / 100).toFixed(2);
        if (!haystack.includes(needle) && !amountText.includes(needle)) return false;
      }

      return true;
    });
  }, [transactions, deferredQuery, filters, showDuplicatesOnly, duplicateIds]);

  const groups = useMemo(
    () => groupByMonth(filtered, { timeZone: settings.timeZone, categories }),
    [filtered, settings.timeZone, categories],
  );

  const activeFilterCount = Object.entries(filters).filter(
    ([key, value]) => key !== 'query' && value !== '',
  ).length;

  return (
    <>
      <div className="stack" style={{ marginTop: 'var(--space-3)' }}>
        <TextInput
          type="search"
          value={filters.query}
          placeholder="Search merchant, note or amount"
          aria-label="Search transactions"
          enterKeyHint="search"
          onChange={(event) => setFilters((current) => ({ ...current, query: event.target.value }))}
        />

        <div className="chip-row">
          <button
            type="button"
            className="chip"
            aria-pressed={activeFilterCount > 0}
            onClick={() => setFiltersOpen(true)}
          >
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </button>
          <button
            type="button"
            className="chip"
            aria-pressed={showDuplicatesOnly}
            onClick={() => setShowDuplicatesOnly((current) => !current)}
          >
            Possible duplicates{duplicateIds.size > 0 ? ` (${duplicateIds.size})` : ''}
          </button>
          {activeFilterCount > 0 || showDuplicatesOnly ? (
            <button
              type="button"
              className="chip"
              onClick={() => {
                setFilters(EMPTY_FILTERS);
                setShowDuplicatesOnly(false);
              }}
            >
              Clear
            </button>
          ) : null}
        </div>
      </div>

      <p className="small muted" style={{ marginTop: 'var(--space-3)' }} role="status">
        {filtered.length === transactions.length
          ? `${transactions.length} ${transactions.length === 1 ? 'payment' : 'payments'}`
          : `${filtered.length} of ${transactions.length} shown`}
      </p>

      {showDuplicatesOnly && duplicateIds.size === 0 ? (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Notice tone="info">No duplicates found. Nothing looks recorded twice.</Notice>
        </div>
      ) : null}

      {groups.length === 0 ? (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <EmptyState
            glyph="🔍"
            title={transactions.length === 0 ? 'No payments yet' : 'Nothing matches'}
            body={
              transactions.length === 0
                ? 'Add a payment from the Today screen and it will appear here.'
                : 'Try a different search, or clear the filters.'
            }
            action={
              transactions.length > 0 ? (
                <Button
                  onClick={() => {
                    setFilters(EMPTY_FILTERS);
                    setShowDuplicatesOnly(false);
                  }}
                >
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        </div>
      ) : (
        groups.map((group) => (
          <section key={group.monthKey} className="section" aria-label={formatMonthLabel(group.monthKey, settings.timeZone)}>
            <div className="month-header">
              <span>{formatMonthLabel(group.monthKey, settings.timeZone)}</span>
              <span className="money">{formatMoney(group.totalSen, { currency: settings.currency })}</span>
            </div>
            <div className="card card--flush">
              {group.transactions.map((transaction) => (
                <TransactionRow
                  key={transaction.id}
                  transaction={transaction}
                  categories={categories}
                  timeZone={settings.timeZone}
                  onSelect={setSelected}
                  now={now}
                />
              ))}
            </div>
          </section>
        ))
      )}

      <Sheet open={filtersOpen} title="Filters" onClose={() => setFiltersOpen(false)}>
        <Field label="Category">
          {(props) => (
            <Select
              {...props}
              value={filters.categoryId}
              onChange={(event) =>
                setFilters((current) => ({ ...current, categoryId: event.target.value }))
              }
            >
              <option value="">All categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Where it came from">
          {(props) => (
            <Select
              {...props}
              value={filters.source}
              onChange={(event) =>
                setFilters((current) => ({ ...current, source: event.target.value }))
              }
            >
              <option value="">Any source</option>
              {PAYMENT_SOURCES.map((source) => (
                <option key={source} value={source}>
                  {sourceLabel(source)}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Paid with">
          {(props) => (
            <Select
              {...props}
              value={filters.account}
              onChange={(event) =>
                setFilters((current) => ({ ...current, account: event.target.value }))
              }
            >
              <option value="">Any account</option>
              {[
                ...new Set([
                  ...accounts.map((account) => account.label),
                  ...transactions.map((transaction) => transaction.accountLabel),
                ]),
              ]
                .filter(Boolean)
                .sort()
                .map((label) => (
                  <option key={label} value={label}>
                    {label}
                  </option>
                ))}
            </Select>
          )}
        </Field>

        <Field label="Type">
          {(props) => (
            <Select
              {...props}
              value={filters.type}
              onChange={(event) =>
                setFilters((current) => ({ ...current, type: event.target.value }))
              }
            >
              <option value="">Any type</option>
              {TRANSACTION_TYPES.map((type) => (
                <option key={type} value={type}>
                  {typeLabel(type)}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <div className="row">
          <div className="grow">
            <Field label="From">
              {(props) => (
                <TextInput
                  {...props}
                  type="date"
                  value={filters.from}
                  onChange={(event) =>
                    setFilters((current) => ({ ...current, from: event.target.value }))
                  }
                />
              )}
            </Field>
          </div>
          <div className="grow">
            <Field label="To">
              {(props) => (
                <TextInput
                  {...props}
                  type="date"
                  value={filters.to}
                  onChange={(event) =>
                    setFilters((current) => ({ ...current, to: event.target.value }))
                  }
                />
              )}
            </Field>
          </div>
        </div>

        <div className="row">
          <Button variant="quiet" className="grow" onClick={() => setFilters(EMPTY_FILTERS)}>
            Reset
          </Button>
          <Button variant="primary" className="grow" onClick={() => setFiltersOpen(false)}>
            Show {filtered.length}
          </Button>
        </div>
      </Sheet>

      <TransactionSheet transaction={selected} onClose={() => setSelected(null)} />
    </>
  );
}
